import { z } from 'zod';
import { cursorPageSchema, cursorQuerySchema, isoDateTimeSchema, localeSchema, uuidSchema, } from './common.js';
import { userStatusSchema } from './enums.js';
import { emailDirectionSchema, emailStatusSchema } from './mail.js';
/**
 * The support inbox (docs/partial/email-and-support-spec.md 6.6): mail to the support address,
 * kept as conversations, read and answered by the roles ADMIN and SUPPORT.
 */
export const supportStatusSchema = z.enum(['OPEN', 'PENDING', 'CLOSED']);
/** `S-10482` – shown in the admin area and carried in every outgoing subject as `[#S-10482]`. */
export const supportTag = (number) => `S-${number}`;
export const supportStaffSchema = z.object({
    id: uuidSchema,
    displayName: z.string(),
    role: z.enum(['ADMIN', 'SUPPORT']),
});
export const supportConversationSchema = z.object({
    id: uuidSchema,
    number: z.number().int(),
    subject: z.string(),
    customerAddress: z.string(),
    customerName: z.string().nullable(),
    status: supportStatusSchema,
    spam: z.boolean(),
    unread: z.boolean(),
    assignedTo: supportStaffSchema.nullable(),
    /** First lines of the newest message, text part only. */
    excerpt: z.string(),
    messageCount: z.number().int(),
    lastMessageAt: isoDateTimeSchema,
    createdAt: isoDateTimeSchema,
});
export const supportConversationPageSchema = cursorPageSchema(supportConversationSchema).extend({
    /** Conversations that need an answer and were not opened yet – the badge. */
    unreadCount: z.number().int(),
});
export const supportConversationQuerySchema = cursorQuerySchema.extend({
    status: supportStatusSchema.optional(),
    q: z.string().max(200).optional(),
    assignedTo: z.union([uuidSchema, z.literal('me'), z.literal('none')]).optional(),
});
export const supportAttachmentSchema = z.object({
    id: uuidSchema,
    fileName: z.string(),
    contentType: z.string(),
    sizeBytes: z.number().int(),
    /** False: too large or too many – listed, not kept. */
    stored: z.boolean(),
});
/** SPF, DKIM and DMARC as the receiving server reported them; a From header can be forged. */
export const supportAuthResultSchema = z.object({
    spf: z.string().nullable(),
    dkim: z.string().nullable(),
    dmarc: z.string().nullable(),
    /** True only when DMARC passed. */
    trusted: z.boolean(),
});
export const supportMessageSchema = z.object({
    id: uuidSchema,
    direction: emailDirectionSchema,
    /** `SYSTEM` notes (e.g. "could not be delivered") are written by the app. */
    kind: z.enum(['MAIL', 'SYSTEM']),
    fromAddress: z.string(),
    toAddress: z.string(),
    subject: z.string(),
    text: z.string(),
    hasHtml: z.boolean(),
    status: emailStatusSchema,
    error: z.string().nullable(),
    sentByName: z.string().nullable(),
    authResult: supportAuthResultSchema.nullable(),
    attachments: z.array(supportAttachmentSchema),
    createdAt: isoDateTimeSchema,
});
/** The account behind the sender's address – a hint, never a proof of identity. */
export const supportAccountHintSchema = z.object({
    id: uuidSchema,
    displayName: z.string(),
    email: z.string(),
    status: userStatusSchema,
    emailVerified: z.boolean(),
    locale: localeSchema,
    createdAt: isoDateTimeSchema,
});
export const supportConversationDetailSchema = supportConversationSchema.extend({
    messages: z.array(supportMessageSchema),
    account: supportAccountHintSchema.nullable(),
});
export const supportConversationUpdateSchema = z.object({
    status: supportStatusSchema.optional(),
    assignedToId: uuidSchema.nullable().optional(),
    unread: z.boolean().optional(),
});
export const supportReplyRequestSchema = z.object({
    text: z.string().trim().min(1).max(20_000),
    /** Quote the customer's last message below the answer. */
    quote: z.boolean().default(false),
    /** Send and close instead of "waiting for customer". */
    close: z.boolean().default(false),
});
export const supportComposeRequestSchema = z
    .object({
    userId: uuidSchema.optional(),
    address: z.string().email().optional(),
    subject: z.string().trim().min(1).max(200),
    text: z.string().trim().min(1).max(20_000),
})
    .refine((v) => Boolean(v.userId) !== Boolean(v.address), {
    message: 'Give either userId or address',
});
export const supportHtmlSchema = z.object({
    /** Sanitized on the server; rendered by the client in a sandboxed frame. */
    html: z.string(),
    /** Remote images were removed from `html`; ask again with `images=1` to keep them. */
    blockedImages: z.number().int(),
});
export const supportAttachmentLinkSchema = z.object({
    url: z.string(),
    expiresAt: isoDateTimeSchema,
});
export const supportSettingsSchema = z.object({
    /** 0 = never delete. */
    retentionDays: z.number().int().min(0).max(3650),
    autoCloseDays: z.number().int().min(1).max(365),
    autoAcknowledge: z.boolean(),
    /** What applies when nothing was saved. */
    defaults: z.object({ retentionDays: z.number().int(), autoCloseDays: z.number().int() }),
});
export const supportSettingsUpdateSchema = z.object({
    retentionDays: z
        .number()
        .int()
        .refine((v) => v === 0 || (v >= 30 && v <= 3650), 'Expected 0 (never) or 30–3650 days')
        .optional(),
    autoCloseDays: z.number().int().min(1).max(365).optional(),
    autoAcknowledge: z.boolean().optional(),
});
export const supportUnreadSchema = z.object({ unreadCount: z.number().int() });
//# sourceMappingURL=support.js.map